import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { DeviceFormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import DebitorForm from "./DebitorForm";
import {
  createSortByAttr,
  DEBITOR_STATUS_LABELS,
  STATUS_IDS,
  ACTION_IDS,
  TableEmptyMessage,
  isMasterOrDL,
} from "../../../../components/Utils";
import TablePageReport from "../../../../components/TablePageReport";

const DebitorListForm = React.forwardRef(function DebitorListForm(
  { className = "", ...restProps },
  ref,
) {
  const debitorsRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    getModel: () => {
      const debitors = debitorsRef.current.getModel();
      const model = [...debitors];
      return model;
    },
    toggleAddition: (disabled) => debitorsRef.current.toggleAddition(disabled),
    setRegions: (pRegions) => debitorsRef.current.setRegions(pRegions),
  }));

  return (
    <div className={className}>
      <Debitor ref={debitorsRef} {...restProps} />
    </div>
  );
});

export default DebitorListForm;

const Debitor = React.forwardRef(function Debitor(
  {
    model = [],
    regions,
    workers,
    contractors,
    getBaseData,
    company,
    getCompany,
    onChange,
    rows = 10,
    disabled: _disabled,
    readOnly,
    className,
  },
  ref,
) {
  const getValidRegions = (regionList, debitors, isCompanySpecialKDL) => {
    const validRegions = [];
    if (regionList && debitors) {
      const debitorCountMap = {};
      const debitorNrMap = {};
      // initialize map
      regionList.forEach((region) => {
        debitorCountMap[region.name] = 0;
      });
      // count nr of debitors pro region
      debitors.forEach((item) => {
        const debKeyTokens = item.debitorKey.split("-");
        const debNr = parseInt(debKeyTokens[debKeyTokens.length - 1]);
        if (debitorCountMap[item.region] === undefined) {
          debitorCountMap[item.region] = 1;
        }
        debitorCountMap[item.region]++;

        if (debitorNrMap[item.region] === undefined) {
          debitorNrMap[item.region] = [];
        }
        debitorNrMap[item.region].push(debNr);
      });
      regionList.forEach((region) => {
        if (
          region.debitorMax > debitorCountMap[region.name] &&
          (isCompanySpecialKDL || (region.debitorKey && region.debitor !== ""))
        ) {
          let suffix = 0;
          if (debitorNrMap[region.name]) {
            const list = debitorNrMap[region.name].sort(
              (a, b) => parseInt(a) - parseInt(b),
            );
            if (list.length === 1) {
              if (list[0] === 1) {
                suffix = 1;
              }
            } else if (list.length > 1) {
              for (let index = 1; index < list.length; index++) {
                if (list[index] - list[index - 1] > 1) {
                  suffix = list[index - 1];
                  break;
                }
              }
              suffix = suffix || list[list.length - 1];
            }
          }
          validRegions.push({ ...region, suffix });
        }
      });
    }
    return validRegions;
  };

  const [state, setState] = React.useState(() => {
    const baseData = getBaseData ? getBaseData() : {};
    const isCompanySpecialKDL = isMasterOrDL(baseData);
    const validRegions = getValidRegions(regions, model, isCompanySpecialKDL);
    return {
      items: model.map((item, index) => ({ ...item, index })),
      regions: validRegions,
      addDisabled: validRegions.length === 0,
      first: 0,
    };
  });

  const formRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    getModel: () => {
      const { items } = state;
      const modelData = items.map((item) => {
        const {
          region,
          debitorKey,
          name,
          comment,
          address,
          person,
          id,
          company_id,
          status_id,
          action_id,
        } = item;
        const debitor = {
          region,
          debitorKey,
          name,
          comment,
          address,
          id,
          company_id,
          status_id,
          action_id,
        };
        if (person) {
          debitor.person = person;
        }
        return debitor;
      });
      return modelData;
    },
    toggleAddition: (disabled) =>
      setState((prev) => ({ ...prev, addDisabled: disabled })),
    setRegions: (pRegions) => {
      const baseData = getBaseData ? getBaseData() : {};
      const isCompanySpecialKDL = isMasterOrDL(baseData);
      const validRegions = getValidRegions(
        pRegions,
        state.items,
        isCompanySpecialKDL,
      );
      const addDisabled = validRegions.length === 0;
      setState((prev) => ({ ...prev, regions: validRegions, addDisabled }));
    },
  }));

  const onSave = (onHide, updating) => {
    const model = formRef.current.getModel();
    if (!model) {
      console.error("Form Validation Error => check your entries...");
      return;
    }
    model.status_id = updating ? STATUS_IDS.UPDATING : STATUS_IDS.NEW;
    model.action_id = updating ? ACTION_IDS.UPDATE : ACTION_IDS.CREATE;
    const items = state.items;
    const newState = {};
    if (model.index !== undefined /*  && items[model.index] !== undefined */) {
      items[model.index] = model;
    } else {
      model.index = items.length;
      items.push(model);
    }
    if (getBaseData) {
      const baseData = getBaseData();
      const isCompanySpecialKDL = isMasterOrDL(baseData);
      newState.regions = getValidRegions(
        baseData.regions,
        items,
        isCompanySpecialKDL,
      );
      newState.addDisabled = newState.regions.length === 0;
    }
    newState.items = items;
    setState((prev) => ({ ...prev, ...newState }));
    onChange && onChange(items);
    onHide && onHide();
  };
  const onDelete = (selected) => {
    if (selected.id) {
      const { items } = state;
      if (items[selected.index]) {
        items[selected.index].status_id = STATUS_IDS.DELETING;
        items[selected.index].action_id = ACTION_IDS.DELETE;
        items[selected.index].status = null; // [STATUS_IDS.DELETING];
        setState((prev) => ({ ...prev, items }));
        onChange && onChange(items);
      } else {
        console.error(
          "<< delphi connection to be deleted was not found",
          state.items,
        );
      }
    } else {
      const items = [];
      state.items.forEach((item) => {
        if (item.debitorKey !== selected.debitorKey) {
          items.push(item);
        }
      });
      setState((prev) => ({ ...prev, items }));
      onChange && onChange(items);
    }
  };

  const disabled = readOnly || _disabled;
  const { first } = state;
  const onPage = (event) => {
    const firstPage = event.first;
    setState((prev) => ({ ...prev, first: firstPage }));
  };

  const renderItemTable = (items, actionTemplate) => {
    const data = items
      .map((item, index) => {
        const copy = JSON.parse(JSON.stringify(item));
        const { street, houseNumber, areaCode, city } = item.address;
        const addressStr = `${item.name}, ${street} ${houseNumber}, ${areaCode} ${city}`;
        const status = item.status || DEBITOR_STATUS_LABELS[item.status_id];
        return {
          ...copy,
          regionName: item.region || item.regionName,
          index,
          addressStr,
          status,
        };
      })
      .sort(createSortByAttr("debitorKey"));
    return (
      <DataTable
        value={data}
        rows={rows}
        paginator={data.length > rows}
        first={first}
        onPage={onPage}
        paginatorLeft={
          <TablePageReport
            className="pad-lft"
            totalRecords={data.length}
            filteredRecords={data.length}
            first={first}
            rows={rows}
            emptyText="Keine Debitoren"
            itemName="Debitor"
            itemNamePlural="Debitoren"
          />
        }
        emptyMessage={<TableEmptyMessage itemNamePlural="Debitoren" min />}
      >
        <Column
          field="regionName"
          header="Region"
          sortable={data.length > 1}
          filter={data.length > rows}
          filterMatchMode="contains"
          style={{ textAlign: "center", width: "8em" }}
        />
        <Column
          field="debitorKey"
          header="Kennung"
          sortable={data.length > 1}
          filter={data.length > rows}
          filterMatchMode="contains"
          style={{ textAlign: "center", width: "10em" }}
        />
        <Column
          field="name"
          header="Firma"
          sortable={data.length > 1}
          filter={data.length > rows}
          filterMatchMode="contains"
          style={{ width: "25%" }}
        />
        <Column
          field="addressStr"
          header="Adresse"
          sortable={data.length > 1}
          filter={data.length > rows}
          filterMatchMode="contains"
        />
        <Column
          field="status"
          header="Freigabestatus"
          sortable={data.length > 1}
          filter={data.length > rows}
          filterMatchMode="contains"
          style={{ textAlign: "center", width: "10em" }}
        />
        <Column
          body={actionTemplate}
          style={{ textAlign: "center", width: "8em" }}
        />
      </DataTable>
    );
  };

  const isEditDisabled = (item) =>
    item.readOnly ||
    (item.id && item.status_id !== STATUS_IDS.FINISHED) ||
    item.inUse;

  const renderItemForm = (item, onHide) => {
    const debitor = item || {
      region: "",
      debitorKey: "",
      name: "",
      address: {
        street: "",
        houseNumber: "",
        areaCode: "",
        city: "",
      },
      comment: "",
      status_id: 1,
    };
    const currentCompany = { ...company };
    const debitorKeys = state.items.map((item) => item.debitorKey);
    const { type_id } = getBaseData();
    const saveDisabled = debitor.status_id !== STATUS_IDS.NEW || debitor.inUse;
    return (
      <div>
        <DebitorForm
          ref={formRef}
          key={Math.random()}
          model={debitor}
          regions={state.regions}
          workers={workers}
          isCompanySpecialKDL={isMasterOrDL({ type_id })}
          contractors={contractors}
          company={currentCompany}
          getCompany={getCompany}
          debitorKeys={debitorKeys}
          disabled={disabled}
        />
        <DeviceFormMenu
          className="margin-big-top w3-border-top pad-big w3-light-grey"
          style={{
            marginLeft: "-1em",
            marginRight: "-1em",
            marginBottom: "-1em",
          }}
          onSave={() => onSave(onHide)}
          onUpdate={() => onSave(onHide, true)}
          onCancel={onHide}
          cancelBtnLabel={
            disabled || isEditDisabled(debitor) ? "Schließen" : undefined
          }
          saveHidden={disabled || isEditDisabled(debitor)}
          updateHidden={disabled || isEditDisabled(debitor) || !saveDisabled}
          saveDisabled={saveDisabled}
          updateDisabled={
            debitor.status_id !== STATUS_IDS.FINISHED || debitor.inUse
          }
          saveBtnLabel="Speichern"
          updateBtnLabel="Ändern"
          saveBtnName="saveDebitorBtn"
          updateBtnName="updateDebitorBtn"
          cancelBtnName="cancelDebitorBtn"
        />
      </div>
    );
  };

  const isDeleteDisabled = (item) =>
    (item.id && item.status_id !== STATUS_IDS.FINISHED) || item.inUse;

  const getItemFormTitle = (debitor) => {
    let title = "hinzufügen";
    if (debitor) {
      title = disabled || isEditDisabled(debitor) ? "anzeigen" : "bearbeiten";
    }
    return <h3 className="no-margin">{`Debitor ${title}`}</h3>;
  };

  return (
    <ItemListManager
      key={Math.random()}
      className={className}
      title={<h3 className="no-margin-top">Debitoren</h3>}
      addBtnLabel="Hinzufügen"
      addBtnName="addCompanyDebitorBtn"
      addBtnDisabled={state.addDisabled}
      addBtnHidden={disabled}
      items={state.items}
      getItemFormTitle={getItemFormTitle}
      renderItemTable={renderItemTable}
      renderItemForm={renderItemForm}
      namePrefix="debitor"
      itemVisibleOnly={disabled}
      isEditDisabled={isEditDisabled}
      itemDeleteable={!disabled}
      hideDisabledDelete
      isDeleteDisabled={isDeleteDisabled}
      deleteTitle="Debitor löschen"
      getDeleteText={(item) =>
        `Wollen Sie den Debitor ${item.debitorKey} unwiderruflich löschen?`
      }
      onDelete={onDelete}
    />
  );
});
